import { differenceInDays, format } from 'date-fns';
import { Decimal } from 'decimal.js';

import type { OfferCharges, PointsConversion } from './types';
import { cashToPoints } from '../../utils/cashToPoints';
import { pointsToCash } from '../../utils/pointsToCash';

export const calculateDailyRate = (totalAmount: string | Decimal, totalNights: number): number => {
  return Number(new Decimal(totalAmount).dividedBy(totalNights));
};

export const calculateDailyPreDiscountAmount = (amount: number | Decimal, nights: number) => {
  return Number(new Decimal(amount).dividedBy(nights).toDecimalPlaces(2));
};

export const calculateDailyTotalAmount = (amount: string | Decimal, totalNights: number): number => {
  return Number(new Decimal(amount).dividedBy(totalNights));
};

export const calculatePointsPaidPercentage = (points: Decimal, amount: string | Decimal) => {
  if (points.greaterThan(0)) {
    return Number(points.dividedBy(new Decimal(amount)).times(100).toFixed(2));
  }
  return 0;
};

export const calculateTotalPointsValue = (totalAmount: string, pointsConversion: PointsConversion): number => {
  return Number(cashToPoints(new Decimal(totalAmount), pointsConversion));
};

export const calculateTotalNights = (checkIn: string | Date, checkOut: string | Date): number => {
  return differenceInDays(new Date(checkOut), new Date(checkIn));
};

export const createLocationString = (city?: string | null, state?: string | null, country?: string | null): string => {
  return `${city ? `${city}, ` : ''}${state ? `${state}, ` : ''}${country}`;
};

export const formatDate = (date: string | Date) => {
  const parsedDate = new Date(date);
  return format(parsedDate, 'yyyy-MM-dd');
};

export const isPromotionExclusive = (promotionName?: string): boolean => {
  return !!promotionName?.toLowerCase().includes('exclusive');
};

export const isPropertyLuxury = (promotionName?: string): boolean => {
  return !!promotionName?.toLowerCase().includes('luxury');
};

export const totalInCashBeforeDiscount = (isPoints: boolean, amount: string, pointsConversion: PointsConversion) => {
  if (isPoints) {
    return Number(pointsToCash(new Decimal(amount), pointsConversion));
  }
  return Number(amount);
};

export const totalNights = (checkIn: string | Date, checkOut: string | Date) => {
  return differenceInDays(new Date(checkOut), new Date(checkIn));
};

export const calculateTotalPointsValueAnyPayWith = (charges: OfferCharges, pointsConversion: PointsConversion): number => {
  const { total, totalCash } = charges;

  if (totalCash?.amount) {
    return total.amount as number;
  }

  return cashToPoints(new Decimal(total.amount), pointsConversion).toNumber();
};

export const getTotalPrice = (charges: OfferCharges): number => {
  const { total, totalCash } = charges;

  if (!totalCash?.amount) {
    return new Decimal(total.amount).toNumber();
  }
  return new Decimal(totalCash.amount).toNumber();
};

export const calculateId = (listName: string): string => {
  return listName
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ') // Replace any character that is NOT a letter, number, or space with a single space
    .replace(/\s+/g, '_') // Replace all sequences of one or more spaces with a single underscore
    .replace(/^_|_$/g, ''); // Remove any leading or trailing underscores that might remain.
};
